import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  useCountryId,
  useLoggedInUserStore,
  useMyStore,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import { useUserPreference } from "@coworker/app/src/hooks/useProfilePreferencesQuery";
import { reportMessageToSentry } from "@coworker/app/src/hooks/useConfigureSentry";
import userPreferences from "@coworker/enums/profilePreferences";
import { getEnvironmentId } from "@coworker/reusable/helpers/environment";
import {
  PENDO_API_KEY,
  PENDO_CDN_BASE_URL,
  PENDO_APPROVED_COUNTRIES_API_URL,
} from "../constants/pendoApprovedCountries";
import { useGraphUserProfile } from "./useGraphUserProfile";

function loadPendoScript(): void {
  if ((window as any).pendo) return;

  const o = ((window as any).pendo = (window as any).pendo || {});
  o._q = o._q || [];
  const methods = [
    "initialize",
    "identify",
    "updateOptions",
    "pageLoad",
    "track",
  ];
  for (const m of methods) {
    o[m] =
      o[m] ||
      function (...args: string[]) {
        o._q[m === methods[0] ? "unshift" : "push"]([m].concat(args));
      };
  }

  const script = document.createElement("script");
  script.async = true;
  script.src =
    PENDO_CDN_BASE_URL + "/agent/static/" + PENDO_API_KEY + "/pendo.js";
  const firstScript = document.getElementsByTagName("script")[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
}

/* ─── SHA-256 hashing ──────────────────────────────────────────────── */

/**
 * Generates a SHA-256 hashed coworker ID from user email.
 * Used for anonymizing user identity in analytics (Pendo).
 */
async function getHashedCoworkerId(mail: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  const Encoder = globalThis.TextEncoder;

  if (!subtle) {
    throw new Error(
      "SHA-256 hashing is unavailable because Web Crypto SubtleCrypto is not supported in this environment."
    );
  }

  if (!Encoder) {
    throw new Error(
      "SHA-256 hashing is unavailable because TextEncoder is not supported in this environment."
    );
  }

  const textAsBuffer = new Encoder().encode(mail);
  const hash = await subtle.digest("SHA-256", textAsBuffer);
  const hashArray = Array.from(new Uint8Array(hash));
  return hashArray.map((item) => item.toString(16).padStart(2, "0")).join("");
}

/* ─── Approved countries API ───────────────────────────────────────── */

interface PendoApprovedCountriesResponse {
  countries: string[];
}

function isValidApprovedCountriesResponse(
  data: unknown
): data is PendoApprovedCountriesResponse {
  if (typeof data !== "object" || data === null) return false;
  const obj = data as Record<string, unknown>;
  const countries = obj["countries"];
  return (
    Array.isArray(countries) &&
    countries.every((c: unknown) => typeof c === "string")
  );
}

async function fetchPendoApprovedCountries(): Promise<string[]> {
  const response = await fetch(PENDO_APPROVED_COUNTRIES_API_URL);

  if (!response.ok) {
    throw new Error(
      `Failed to fetch Pendo approved countries (${response.status})`
    );
  }

  const data: unknown = await response.json();

  if (!isValidApprovedCountriesResponse(data)) {
    reportMessageToSentry(
      "Invalid pendo approved countries response data",
      { data },
      "error"
    );
    throw new Error("Invalid pendo approved countries response data");
  }

  return data.countries;
}

function usePendoApprovedCountries() {
  return useQuery({
    queryKey: ["pendoApprovedCountries"],
    queryFn: fetchPendoApprovedCountries,
    enabled: isProductionEnvironment(),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 2,
  });
}

/* ─── Production check ─────────────────────────────────────────────── */

function isProductionEnvironment(): boolean {
  const envId = getEnvironmentId();
  return envId.endsWith("-prod") || envId === "prod";
}

/* ─── isPendoEnabled ───────────────────────────────────────────────── */

/**
 * Determines whether Pendo should initialize; always true outside production,
 * otherwise requires the user's country to be in the approved-countries list.
 */
function isPendoEnabled(
  pendoApprovedCountries: string[] | null | undefined,
  usageLocation: string | null
): boolean {
  if (!usageLocation) {
    return false;
  }

  // Always enable when env is not production
  if (!isProductionEnvironment()) {
    return true;
  }

  if (!pendoApprovedCountries) {
    return false;
  }

  return pendoApprovedCountries.includes(usageLocation);
}

/* ─── Main hook ────────────────────────────────────────────────────── */

export function usePendo() {
  const usageLocation = useCountryId();
  const email = useLoggedInUserStore((state) => state.email);
  const store = useMyStore();
  const [selectedLanguage] = useUserPreference(
    userPreferences.LANGUAGE,
    store?.primaryLocale ?? ""
  );
  const { data: graphProfile } = useGraphUserProfile();
  const { data: approvedCountries } = usePendoApprovedCountries();

  const pendoInitialized = React.useRef<boolean>(false);
  const graphProfileRef = React.useRef(graphProfile);

  React.useEffect(() => {
    graphProfileRef.current = graphProfile;
  }, [graphProfile]);

  React.useEffect(() => {
    if (pendoInitialized.current) return;
    if (!email || !usageLocation) return;

    if (!isPendoEnabled(approvedCountries, usageLocation)) return;

    loadPendoScript();

    getHashedCoworkerId(email)
      .then((hashedEmail) => {
        if (pendoInitialized.current) return;
        if (!(window as any).pendo?.initialize) return;

        const profile = graphProfileRef.current;
        (window as any).pendo.initialize({
          visitor: {
            id: hashedEmail,
            jobtitle: profile?.jobTitle ?? undefined,
            department: profile?.department ?? undefined,
            primarysite: profile?.officeLocation ?? undefined,
            country: usageLocation,
            businessunit: profile?.companyName ?? undefined,
            role: "application_user",
            selectedlanguage: selectedLanguage,
          },
        });
        pendoInitialized.current = true;
      })
      .catch((e) => {
        console.warn("Failed to initialize Pendo", e);
      });
  }, [email, usageLocation, selectedLanguage, approvedCountries]);

  React.useEffect(() => {
    if (!pendoInitialized.current) return;
    if (!graphProfile) return;
    if (!(window as any).pendo?.updateOptions) return;

    try {
      (window as any).pendo.updateOptions({
        visitor: {
          jobtitle: graphProfile.jobTitle ?? undefined,
          department: graphProfile.department ?? undefined,
          primarysite: graphProfile.officeLocation ?? undefined,
          businessunit: graphProfile.companyName ?? undefined,
        },
      });
    } catch (e) {
      console.warn("Failed to update Pendo with graph profile", e);
    }
  }, [graphProfile]);

  React.useEffect(() => {
    if (!pendoInitialized.current || !selectedLanguage) return;
    if (!(window as any).pendo?.getSerializedMetadata) return;

    try {
      const pendoMetadata = (window as any).pendo.getSerializedMetadata();
      if (!pendoMetadata) return;

      (window as any).pendo.updateOptions({
        ...pendoMetadata,
        visitor: {
          ...pendoMetadata.visitor,
          selectedlanguage: selectedLanguage,
        },
      });
    } catch (e) {
      console.warn("Failed to update Pendo options", e);
    }
  }, [selectedLanguage]);
}
